import {cloneDeep} from 'lodash';
import {useEffect, useState} from 'react';

import {useFetchTournamentRoundsQuery} from '@api';
import {TournamentPlayerRoundResult} from '@types';

export const useTournamentResults = (
    tournamentId: string,
    options: {
        showResultWithZeroScore?: boolean;
        skip?: boolean;
        polling?: boolean;
    } = {}
) => {
    const [results, setResults] = useState<TournamentPlayerRoundResult[]>([]);
    const {
        currentData: roundsData,
        refetch,
        isLoading,
        isError,
    } = useFetchTournamentRoundsQuery(
        {
            tournamentId,
        },
        {
            skip: options.skip,
            pollingInterval: options.polling ? 3000 : undefined,
            skipPollingIfUnfocused: true,
            refetchOnMountOrArgChange: true,
            refetchOnFocus: true,
        }
    );
    useEffect(() => {
        if (!roundsData) {
            setResults([]);
            return;
        }

        const roundResults: TournamentPlayerRoundResult[] = [];
        const sortedRoundsData = cloneDeep(roundsData).sort(
            (a, b) => a.number - b.number
        );
        for (const round of sortedRoundsData) {
            for (const result of round.results) {
                const index = roundResults.findIndex(
                    r => r.player.user.id === result.player.user.id
                );

                if (index === -1) {
                    roundResults.push(result);
                } else {
                    const existingResult = roundResults[index];

                    if (result.roundNumber > existingResult.roundNumber) {
                        if (
                            (options.showResultWithZeroScore === false &&
                                result.score > 0) ||
                            options.showResultWithZeroScore !== false
                        ) {
                            roundResults[index] = {
                                ...existingResult,
                                player: result.player,
                                roundNumber: result.roundNumber,
                                score: result.score,
                                livesLeft: result.livesLeft,
                                timeSpent: result.timeSpent,
                                promoted: result.promoted,
                            };
                        }
                    }
                }
            }
        }

        roundResults.sort((a, b) => {
            if (b.roundNumber !== a.roundNumber) {
                return b.roundNumber - a.roundNumber;
            }
            if (b.score !== a.score) {
                return b.score - a.score;
            }
            if (a.livesLeft !== b.livesLeft) {
                return b.livesLeft - a.livesLeft;
            }
            return a.timeSpent - b.timeSpent;
        });

        setResults(roundResults);
    }, [roundsData, options.showResultWithZeroScore]);

    return {results, refetch, isLoading, isError};
};
